import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { UserX, Briefcase, Star, AlertCircle } from "lucide-react";
import { API_URL } from "../components/config"; // Adjust the import based on your project structure
const EmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/users/employees/roles`
      );
      const data = await response.json();
      data.sort((a, b) => a.grade - b.grade);
      setEmployees(data);
      setError(null);
    } catch (error) {
      setError("Failed to fetch employees");
      console.error("Error fetching employees:", error);
    } finally {
      setLoading(false);
    }
  };

  const terminateEmployee = async (userid, username) => {
    if (window.confirm(`Are you sure you want to terminate ${username}?`)) {
      try {
        const response = await fetch(
          `${API_URL}/users/terminate/${userid}`,
          {
            method: "PUT",
          }
        );
        const result = await response.json();

        if (response.ok) {
          // Show success message
          const successMessage = document.createElement("div");
          successMessage.className =
            "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg";
          successMessage.textContent = result.message;
          document.body.appendChild(successMessage);
          setTimeout(() => successMessage.remove(), 3000);

          fetchEmployees();
        } else {
          throw new Error(result.message);
        }
      } catch (error) {
        // Show error message
        const errorMessage = document.createElement("div");
        errorMessage.className =
          "fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg";
        errorMessage.textContent =
          error.message || "Failed to terminate employee";
        document.body.appendChild(errorMessage);
        setTimeout(() => errorMessage.remove(), 3000);
      }
    }
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const item = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1 },
  };

  if (loading) {
    return (
      <div
        className="flex justify-center items-center h-screen"
        style={{ backgroundColor: "#f8f9fa" }}
      >
        <div
          className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4"
          style={{ borderColor: "#ffc000" }}
        ></div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="flex justify-center items-center h-screen"
        style={{ backgroundColor: "#f8f9fa" }}
      >
        <div className="text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <p className="text-xl text-red-500">{error}</p>
          <button
            onClick={fetchEmployees}
            className="mt-4 px-6 py-2 rounded-lg text-white"
            style={{ backgroundColor: "#002966" }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen py-8 px-4"
      style={{ backgroundColor: "#f8f9fa" }}
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="mb-8 text-center"
        >
          <h1 className="text-3xl font-bold mb-2" style={{ color: "#002966" }}>
            Employee List
          </h1>
          <p className="text-gray-600">Manage your organization's workforce</p>
        </motion.div>

        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {employees.map((employee) => (
            <motion.div
              key={employee.userid}
              variants={item}
              className="bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:shadow-xl"
              style={{ borderTop: `4px solid #ffc000` }}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2
                    className="text-xl font-semibold"
                    style={{ color: "#002966" }}
                  >
                    {employee.username}
                  </h2>
                  <div className="flex items-center">
                    <Star className="w-5 h-5" style={{ color: "#ffc000" }} />
                    <span
                      className="ml-1 font-semibold"
                      style={{
                        color:
                          employee.status === "terminated" ? "red" : "#002966",
                      }}
                    >
                      Status {employee.status}
                    </span>
                  </div>
                </div>

                <div className="flex items-center mb-4">
                  <Briefcase
                    className="w-5 h-5 mr-2"
                    style={{ color: "#002966" }}
                  />
                  <span className="text-gray-600">{employee.role}</span>
                </div>

                <button
                  onClick={() =>
                    terminateEmployee(employee.userid, employee.username)
                  }
                  className="w-full mt-4 px-4 py-2 rounded-lg flex items-center justify-center text-white transition-all duration-300 hover:opacity-90"
                  style={{ backgroundColor: "#002966" }}
                >
                  <UserX className="w-5 h-5 mr-2" />
                  Terminate Employee
                </button>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {employees.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <p className="text-gray-500 text-lg">No employees found</p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default EmployeeList;
